/*
    This file is used to simulate "Sitecore Data" when running in "Sitecore Frame => EikaView.js" (localhost, app-test, app-qa).
    NB: All data that comes from Sitecore are text and use "double quotes", so here it also need to return strings.
*/
import { DeviceType } from '@generated-types';

import advantages from './sections/advantages';
import APIErrorMessages from './sections/api-error-codes';
import blockCard from './sections/block-card';
import cardActivation from './sections/card-activation';
import cardControl from './sections/card-control';
import cardDetails from './sections/card-details';
import changeCreditLimit from './sections/change-credit-limit';
import closeCreditAccount from './sections/close-credit-account';
import creditCardApplication from './sections/credit-card-application';
import digitalCard from './sections/digital-card';
import documents from './sections/documents';
import form from './sections/form';
import genericErrors from './sections/generic-errors';
import googleWallet from './sections/google-wallet';
import keepAlive from './sections/keep-alive';
import loading from './sections/loading';
import menuWidgets from './sections/menu-widgets';
import orderCard from './sections/order-card';
import overview from './sections/overview';
import pin from './sections/pin';
import reduction from './sections/reduction';
import spendingLimits from './sections/spending-limits';
import thirdPartyDependencies from './sections/third-party-dependencies';
import transactions from './sections/transactions';
import transfer from './sections/transfer';
import userInfo from './sections/user-info';
import valueProgram from './sections/value-program';
import visaCalculator from './sections/visa-calculator';

import click2pay from './sections/click2pay';

const sitecoreSimulationData = {
    loggedInReturnUrl: '',
    purchaseAppPath: '',
    skjema: {},
    text: {
        ...advantages,
        ...APIErrorMessages,
        ...blockCard,
        ...cardControl,
        ...cardDetails,
        ...changeCreditLimit,
        ...closeCreditAccount,
        ...creditCardApplication,
        ...digitalCard,
        ...documents,
        ...form,
        ...genericErrors,
        ...googleWallet,
        ...keepAlive,
        ...loading,
        ...menuWidgets,
        ...orderCard,
        ...overview,
        ...pin,
        ...reduction,
        ...spendingLimits,
        ...thirdPartyDependencies,
        ...transactions,
        ...transfer,
        ...userInfo,
        ...valueProgram,
        ...visaCalculator,
        ...cardActivation,
        ...click2pay,

        /* Common */
        tryAgain: 'Prøv igjen',
        productName: 'Kortbetjening',
        done: 'Ferdig',
        locked: 'Låst',
        confirm: 'Bekreft',
        cancel: 'Avbryt',
        close: 'Lukk',
        undo: 'Angre',
        goOn: 'Gå videre',

        bankkortNameVisaClassic: 'Classic bankkort',
        bankkortNameVisaOnline: 'Online bankkort',
        bankkortNameVisaElectron: 'Electron bankkort',

        defaultInfoBoxText:
            'Ta kontakt med Kredittbanken på telefon 915 03 850 eller e-post kundesenter@eika.no hvis du har spørsmål. Vi hjelper deg gjerne!',

        direkteKanalBanker: '0411,0770,0413,0416,1821,9812',

        /* Card Select */
        cardSelectDefaultTitle: 'Velg kort',
        cardSelectNoAvailable: 'Ingen aktuelle kort for denne handlingen',
        cardSelectAvailable: 'Disponibelt beløp',
        cardSelectSaldo: 'Saldo',

        /* Card order status */
        cardStatusIntervalDays: '14',

        /* Transfer */
        transferTitle: 'Overfør fra kort til konto',
        transferTitle2: 'Status på overføringer',
        transferIngress: 'Her kan du overføre valgt beløp til din konto.',
        transferIngress2:
            'Et gebyr på kr {fee} vil komme i tillegg til overføringen, og renter påregnes fra første dag.',
        transferIngress3: 'Det vil ta opptil et par virkedager før overføringen er tilgjengelig på konto.',
        transferChargeInfo: '100 kr i gebyr kommer i tillegg',
        transferErrorHeader: 'Noe gikk galt',
        transferErrorContent: 'Vi kunne ikke gjennomføre overføringen akkurat nå. Vennligst prøv igjen senere.',
        transferErrorAccountInvalid: 'Vennligst fyll inn et gyldig kontonummer',
        transferErrorAccountIllegal: 'Kan ikke overføre til dette kontonummeret',
        transferErrorAmountInvalid: 'Vennligst fyll inn et gyldig beløp',
        transferErrorAmountTooLow: 'Beløpet kan ikke være mindre enn {minAmount}',
        transferErrorAmountTooHigh: 'Beløpet kan ikke være større enn {maxAmount}',
        transferErrorAmountHigherThanCredit:
            'Beløpet kan ikke være større enn det du har disponibelt på kortet inkludert gebyr',
        transferErrorCommentIllegal: 'Kommentar inneholder ulovlige tegn',
        transferErrorCommentTooLong: 'Melding kan ikke være lenger enn 1024 tegn',
        transferErrorKidIllegalLength: 'KID må være mellom 2 og 25 tegn',
        transferErrorKidNotValid: 'KID-nummer er ikke gyldig',
        transferErrorCardMissing: 'Fant ikke kortet',
        transferErrorCardDebit: 'Du må velge kredittkort ved overføring',
        transferMessageButtonNew: 'Ny overføring',
        transferMessageButtonTryAgain: 'Prøv på nytt',
        transferConfirmHeader: 'Overføring sendt!',
        transferConfirmMessage1: 'fra ditt',
        transferConfirmMessage2: 'til kontonummer',
        transferConfirmMessage3: 'er sendt til overføring.',
        transferFormAccountNumber: 'Til konto',
        transferFormManualAccountNumber: 'Kontonummer',
        transferFormAmount: 'Beløp',
        transferFormComment: 'Kommentar',
        transferFormKid: 'KID',
        transferFormKidDescription: 'KID brukes kun for betaling til ekstern konto som krever dette',
        transferFormConfirm: 'Bekreft',
        transferFormError: 'Noe gikk galt under innsending. Sjekk skjemaet og prøv på nytt.',
        transferFormAccountTooltip: 'Hvilket kontonummer skal beløpet overføres til?',
        transferFormAmountTooltip: 'Beløp kan ikke være mindre enn kr {minAmount} og ikke mer enn kr {maxAmount}',
        transferFormDropdownAccount: 'Bankkonto',
        transferListFromCard: 'Fra kort',
        transferListFromAccount: 'Fra konto',
        transferListToAccount: 'Til konto',
        transferListAmount: 'Beløp',
        transferListComment: 'Kommentar',
        transferListDate: 'Dato/Status',
        transferListKid: 'KID',
        transferListRegDate: 'Registreringsdato',
        transferListUpdateDate: 'Sist oppdatert',
        transferListStatus: 'Status',
        transferListNoData: 'Ingen tidligere overføringer',
        transferListError: 'Kunne ikke hente tidligere overføringer',
        transferListProcessing: 'Behandles',
        transferListCanceled: 'Avbrutt',
        transferListDeclined: 'Avslått',
        transferDetailsTitle: 'Detaljer',
        transferAccountDropdownOther: 'Annet kontonummer',

        transferTitleKredittbanken: 'Overfør fra kort',
        paymentTitleKredittbanken: 'Betal og overfør fra kort',

        /* Errormeldinger Kort til konto */
        transferErrorInternalError: 'Ukjent feil',
        transferErrorMoneyTransferNotFound: 'Fant ikke overføring',
        transferErrorAccountPaymentOverdue: 'Kontoen er i purreforløp',
        transferErrorTransferLimitExceeded: 'Grense for denne typen overføring nådd',
        transferErrorAccountInsufficientFunds: 'Det er ikke dekning for beløpet',
        transferErrorTransferAccountInvalid: 'Det kan ikke overføres til den valgte kontoen',
        transferErrorNoOCR: 'Kunde-ID (KID) skal ikke fylles ut fordi det ikke finnes noen OCR-avtale',

        /* Valgfri design */
        customDesignTitle: 'Valgfri design',
        customDesignIngress: 'Her kan du bestemme design på kortet ditt',
        customDesignLinkText: 'Klikk her for å gå til kortdesigneren',
        customDesignLinkSubText: 'Åpnes i nytt vindu',
        customDesignError: 'Vi beklager! Vi kunne ikke gjennomføre tjenesten akkurat nå. Vennligst prøv igjen senere.',

        /* Card Info */
        cardNameGold: 'Gold kredittkort',
        cardNameBusiness: 'Business kredittkort',

        /* External Service Error */
        serviceErrorHeader: 'Vi beklager',
        serviceErrorText1: 'Vi klarte ikke å kommunisere med ekstern tjeneste.',
        serviceErrorText2:
            'Du kan prøve å laste siden på ny og prøve igjen. Hvis det fortsatt er feil, så må du dessverre vente en stund. Vi er på saken og prøver å løse feilen så fort som mulig.',
        serviceErrorReturnButton: 'Last siden på ny',
        serviceErrorReturnLink: './',
        serviceErrorInfoBox: '{defaultInfoBoxText}',

        /* Kort Error */
        cardErrorHeader: 'Vi beklager',
        cardErrorText1: 'Vi har litt problemer med å hente ut kortinformasjon akkurat nå.',
        cardErrorText2:
            'Du kan gå tilbake til forrige steg og prøve igjen. Hvis det fortsatt er feil, så må du dessverre vente en stund. Vi er på saken og prøver å løse feilen så fort som mulig.',
        cardErrorText3: '',
        cardErrorReturnButton: 'Gå tilbake til {productName}',
        cardErrorReturnLink: '/betale/kredittkort',
        cardErrorInfoBox: '{defaultInfoBoxText}',

        cardListEmptyTitle: 'Du har ingen kort',
        cardListEmptyText: 'Du kan enkelt bestille kort, velg det som passer deg best.',
        cardListErrorText: 'Vi klarte dessverre ikke å hente kortene dine, vennligst prøv igjen om litt',

        /* 404 Page not found */
        pageNotFoundTitle: 'Siden finnes ikke',
        pageNotFoundText1: 'Vi beklager så mye, men vi finner ikke siden du leter etter.',
        pageNotFoundText2: '',
        pageNotFoundText3: 'Vårt beste tips er å bruke menyen, håper at du finner det du leter etter der.',

        /** * customAppTypes! ** */

        /* Namespace NAF: */
        productName_naf: 'NAF Kortbetjening',
        defaultInfoBoxText_naf:
            'Ta kontakt med Kredittbanken på telefon 915 03 850 eller e-post kundesenter@eika.no hvis du har spørsmål. Vi hjelper deg gjerne!',
        cardsGold_naf: 'knafv01',
        cardsBusiness_naf: '',
        cardNameGold_naf: 'NAF Xtra kort',

        /* Namespace LBA: */
        productName_lbkredittkort: 'LBA Kortbetjening',
        defaultInfoBoxText_lbkredittkort:
            'Ta kontakt med Kredittbanken på telefon 915 03 850 eller e-post kundesenter@eika.no hvis du har spørsmål. Vi hjelper deg gjerne!',
        cardsGold_lbkredittkort: 'klbpr01',
        cardsBusiness_lbkredittkort: '',
        cardNameGold_lbkredittkort: 'LBA',

        /* Namespace: EIKA */
        productName_eika: 'Eika Kortbetjening',
        defaultInfoBoxText_eika:
            'Ta kontakt med Kredittbanken på telefon 915 03 850 eller e-post kundesenter@eika.no hvis du har spørsmål. Vi hjelper deg gjerne!',
        cardNameGold_eika: 'Eika Gold kredittkort',
        cardNameBusiness_eika: 'Eika Business kredittkort',

        frontPageTitle: 'Mine kredittkort',
        frontPageShowAllLink: '/min-oversikt/kort',
        frontPageShowAllLinkText: 'Se alle kort',
        frontPageCreditCardsTitle: 'Kredittkort',

        yourCard: 'Kortet ditt',
        myCardTitle: 'Mitt kort',
        myCardIngress: 'Her kan du se informasjon om kortet ditt',
        myCardAvailableCredit: 'Tilgjengelig kreditt',
        myCardUsedCredit: 'Benyttet',
        myCardShowMore: 'Vis mer',
        myCardShowLess: 'Vis mindre',
        myCardCardInfoLink: 'Betjen mitt kort',
        myCardCardInfoTitle: 'Snarveier',
        myCardCardInfoNoShortcuts: 'Ingen snarveier tilgjengelig for dette kortet',

        /* complaint */
        complaintTitle: 'Reklamasjon ved svindel eller tvist',
        complaintDescription:
            'Har du fått belastet et beløp dobbelt, ikke mottatt vare etter bestilling på internett eller er utsatt for svindel på dine kort kan du be om å få tilbakebetalt beløpet gjennom reklamasjon.',
        complaintNote:
            'Reklamasjonen opprettes i Nets sin portal i eget vindu. Bruk BankID, les gjennom informasjonen og ha dokumentasjon tilgjengelig.',
        complaintLink: 'Opprett reklamasjon',

        /* CustomerBenefits */
        customerBenefitsTitle: 'Kundefordeler',

        /* insuranceProof */
        insuranceProofHeader: 'Forsikringsdetaljer',
        insuranceProofTitle: 'Bevis for kredittkortforsikringen',
        insuranceProofDescription:
            'Har du betalt minimum 50 % av reisens utgifter til transport og /eller overnatting med kredittkortet har du automatisk reise- og avbestillingsforsikring inkludert og for reiser i Norge.',
        insuranceProofNumber: 'Forsikringsnummer',
        insuranceProofNumberValue: '590501',
        insuranceProofMedicineExpense: 'Medisinske utgifter (NOK)',
        insuranceProofMedicineExpenseValue: 'Ubegrenset',
        insuranceProofHomeTransport: 'Hjemtransport (NOK)',
        insuranceProofHomeTransportValue: 'Ubegrenset',
        insuranceProofCompany: 'Selskap',
        insuranceProofCompanyName: 'Fremtind Forsikring, SOS International',

        /* commercial */
        commercialCreditCardTitle: 'Kredittkort',
        commercialCreditCardText: 'Gebyrfritt kort med inntill 100 000 kr i kreditt',
        commercialCreditCardButton: 'Søk om kredittkort',
        commercialCreditCardMore: 'Mer om kredittkortet',
        commercialInsuranceProofTitle: '',
        commercialInsuranceProofText: 'Les mer om forsikringen på nettsidene våre. ',
        commercialInsuranceProofLink: 'Gå til nettsiden',

        /* creditCardLimit */
        creditCardLimitDecreasing: 'Senk grensen',
        creditCardLimitIncreasing: 'Øk grensen',
        creditCardLimitLeadText: 'Din kredittgrense',
        creditCardLimitMessageTipsTitle: 'Tenk deg om før du justerer kredittgrensen din',
        creditCardLimitMessageTipsText:
            'Sørg for at den er romslig nok til å dekke månedlige abonnementer, nettkjøp, reisebestillinger og forbruket ditt under selve reisen.',

        /* message */
        messageErrorOptionRequired: 'Velg ett alternativ for å gå videre',
        messageErrorOrderOptionRequired: 'Du må velge hvilket kort du vil bestille',
        messageErrorOrderAccountOptionRequired: 'Du må velge en konto for å gå videre',
        messageErrorText2:
            'Vi beklager, det har skjedd en teknisk feil så du kan ikke sjekke kortene dine akkurat nå. Prøv igjen litt senere.',
        messageErrorText: 'Noe gikk galt',
        messageErrorUps: 'Ojsann!',
        messageSuccessSaved: 'Endring er lagret',
        messageSuccessOrderCard: 'Kortet er bestilt',
        messageSuccessReopenCard: 'Kortet er åpnet',
        messageSecondLeft: 'sekunder igjen',
        messageStatusSentToCustomer: 'Kortet ditt er sendt',
        messageStatusSentToProduction: 'Kortet er sendt til produksjon',
        messageStatusProductionStarted: 'Produksjonen har startet',
        messageStatusOrdered: 'Bestillingen er sendt',
        messageOrderCardPending:
            'Nytt kort er bestilt, men det er ikke klart riktig enda. Det kan ta opp til et par dager før det er klart. Ta kontakt med banken din hvis problemet vedvarer.',
        messageEmptyInformation: 'Ingen informasjon',
        messageComingSoonTitle: 'Vi pusser opp',
        messageComingSoonText:
            'Denne tjenesten er ikke helt på plass her i appen ennå. I mellomtiden må du logge inn i den gamle mobilbanken for å administrere denne tjenesten.',
        messageCardPendingAndSoftBlocked: ', men det er midlertidig sperret.',
        messageCardPendingAndHardBlocked: ', men det er sperret. Husk å klipp det i stykker når du får det!',
        systemUpgradeMessage:
            'Grunnet systemoppgradering er bestilling av bankkort via mobil og nettbank dessverre utilgjengelig i perioden 2-14 september. Vennligst kontakt banken dersom du trenger nytt kort. Bestilling av kredittkort fungerer som normalt.',
        cardInformationFetchErrorMessage: 'Noe gikk galt ved innhenting av kortinformasjonen.',

        /* Payment */
        paymentUrlPath: '/nettbank/betaling/ny-betaling',
        paymentUrlPathV2: '/nettbank/betale/ny-betaling',
        paymentUrlParamAccountId: 'receiverAccountNumber',
        paymentUrlAccountNumber: '70500662172',
        paymentUrlParamKid: 'kid',
        paymentUrlParamReturnUrl: 'returnUrl',
        paymentDelinquentAccountError:
            'Overføringen feiler fordi du har en ubetalt kredittkortfaktura. Før du kan overføre penger fra kredittkortet ditt til annen konto, må du betale fakturaen og innbetalingen må være registrert hos oss.',

        // insurance
        insurancePageTitle: 'Reiseforsikring',
        insuranceInfoTitle: 'Om reiseforsikringen',
        insuranceInfoText1: 'Forsikringsbeviset gjelder kun for deg som er kredittkortkunde.',
        insuranceInfoText2:
            'Du har gyldig reise- og avbestillingsforsikring dersom minimum 50 % av reisen er betalt med kredittkortet.',
        insuranceHighlightedText1: 'Ved forsinkelse av bagasje, tyveri av reisegods og andre skadehenvendelser',
        insuranceHighlightedText2: 'Ved akutt sykdom, alvorlige ulykker, nødvendig hjemtransport eller død',
        insuranceEikaInsuranceContactHeader: 'Eika Forsikring',
        insuranceEikaInsuranceContactPhone: 'Telefon: +47 915 03 850',
        insuranceEikaInsuranceContactEmail: 'E-post: forsikring@eika.no',
        insuranceEikaInsuranceContactPostbox: 'Postboks 332, 2303 Hamar',
        insuranceSOSTitle: 'SOS International',
        insuranceSOSPhone: 'Telefon: +45 70 10 50 50',
        insuranceSOSFax: 'Fax: +45 70 10 50 56',
        insuranceSOSEmail: 'E-post: sos@sos.dk',
        insuranceSOSAddressLine1: 'Nitivej 6, DK-2000',
        insuranceSOSAddressLine2: 'Frederiksberg, Danmark',
        insuranceSOSText: 'SOS er en døgnåpen alarmsentral.',
        insuranceLinkToCardOverviewText: 'Gå til kort oversikten',
        insuranceLinkToCardOverviewLink: '/min-oversikt/kort',

        /* Google Tap And Pay */
        googleAddToWalletText: 'Google Pay',

        /* Apple pay */
        addToAppleWallet: 'Legg til i Apple Lommebok',
        cardAddedToWallet: 'Kortet er lagt til i Apple Lommebok.',
        cardAddedToWalletOn: 'Kortet er lagt til i Apple Lommebok på {}{}.',
        and: 'og',
        cardNotAddedToWallet: 'Kortet ble ikke lagt til i Apple Lommeboken',
        appleWalletSuccessDescription:
            'Apple Pay er en enklere måte å betale på i butikker, apper og på nett med din iPhone, Apple Watch, iPad og Mac.' +
            '\n \n' +
            'Apple Pay kan benyttes der du ser disse symbolene:',
        tapToPay: 'Kontaktløs betaling',
        applePay: 'Apple pay logo',
        applePayBankInfo: 'Som kunde i denne banken kan du nå betale med iPhonen din',
        chooseCardToAddToAppleWallet: 'Velg kortet du ønsker å koble til Apple Lommebok',
        onlyOneCardToAddToAppleWallet: 'Du har kun ett bankkort som kan legges i Apple Lommebok',
        notNow: 'Ikke nå',
        findApplePayAtCardPage: 'Passer det ikke nå, finner du Apple Lommebok på kortsidene dine',
        youCanAlsoAddYourCardTo: 'Du kan også legge inn kortet på {}',
        toDoThisClickTheLinkAbove: 'For å gjøre dette må du følge lenken over',
        news: 'Nyhet',
        youCanUseApplePay: 'Du kan bruke Apple Pay',
        simplerPaymentsFromMobile: 'Enda enklere handleturer med betaling rett fra mobiltelefonen. Kom i gang her!',

        /* Pending cards */
        activateApplePayCard: 'Godkjenn innrullering av kort i Apple Lommebok',
        doYouWishToAddTheCard: 'Velg det kortet du ønsker å ferdigstille innrulleringen på',
        [`applePayDevice:${DeviceType.MOBILE_PHONE}`]: 'iPhone',
        [`applePayDevice:${DeviceType.TABLET}`]: 'iPad',
        [`applePayDevice:${DeviceType.WATCH}`]: 'Apple Watch',
        [`applePayDevice:${DeviceType.MOBILEPHONE_OR_TABLET}`]: 'iPhone',
        [`applePayDevice:${DeviceType.UNKNOWN}`]: 'iPhone',

        iPhone: 'iPhone',
        watch: 'Apple Watch',
        iPad: 'iPad',
        wasItYou: 'Er det ikke deg som har startet innrulleringen?',
        clickAndContactBank: 'Trykk {} og kontakt banken',
        ifThisHappenedByMistake: 'Om dette skjedde ved en feil, kan du trykke {} for å gå tilbake.',
        areYouSureYouWantToCancelEnrollment: 'Er du sikker på at du ikke vil innrullere kortene?',
        couldNotActivateCard: 'Kunne ikke aktivere kort. Prøv igjen.',
        activate: 'Aktiver',
        activated: 'Aktivert',
        butLacksVerification: ', men mangler verifisering',
        butBothLacksVerification: ', men begge mangler verifisering',
        butAllLacksVerification: ', men alle mangler verifisering',
        butTheseLacksVerification: ', men {} mangler verifisering',
        completeVerificationIPhone: 'Fullfør verifisering på iPhone',
        completeVerificationIPad: 'Fullfør verifisering på iPad',
        completeVerificationWatch: 'Fullfør verifisering på Apple Watch ',

        /* Errors */
        errorCardWithIdNotFound: 'Ingen kort med denne IDen finnes.',

        debitCardsTitle: 'Mine Bankkort',
        creditCardsTitle: 'Mine Kredittkort',
        sharedCardsTitle: 'Kort knyttet til felleskontoer',
    },
};

export default sitecoreSimulationData;
